import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Activity,
  BadgeCheck,
  Briefcase,
  Building2,
  Calendar,
  Landmark,
  Map,
  MapPin,
  Network,
  SlidersHorizontal,
  Tag,
  Ticket,
  UserCog,
  UserPlus,
  Users,
  UsersRound,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errorMessage, toQuery } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useEvents, useExpertises, useGroupIndex, useGroups, useOrgRefs, usePersistentView, useProgrammations, useUrlState } from "@/hooks/useDirectory";
import { PageHeader, Pager, SearchInput, ViewToggle } from "@/components/common/Layout";
import { ActiveChips, CityFilter, MultiSelectFilter, OrgFilter } from "@/components/filters/Filters";
import { EmptyState, ErrorState, LoadingGrid } from "@/components/common/States";
import { PersonCard, PersonTable } from "@/components/persons/PersonViews";
import { SelectionBar } from "@/components/persons/SelectionBar";
import { formatNumber } from "@/lib/format";

const KEYS = [
  "organization",
  "club",
  "commission",
  "group",
  "expertise",
  "business_department",
  "profile_type",
  "activites",
  "programmation_ids",
  "evenement_ids",
  "membership",
  "ville",
  "geo_department",
];

const ACTIVITE_OPTIONS = [
  { value: "Intervention", label: "Intervention" },
  { value: "Animation", label: "Animation" },
  { value: "Participation", label: "Participation" },
];

const PAGE_SIZE = 24;

export default function PersonsPage() {
  const { isAuthed } = useAuth();
  const url = useUrlState(KEYS);
  const [view, setView] = usePersistentView("lm-persons-view");
  const [selected, setSelected] = useState(() => new Set());
  const [showFilters, setShowFilters] = useState(true);

  const job = url.sp.get("job") || "";
  const ville = url.sp.get("ville") || url.sp.get("city") || "";

  const { data: groups } = useGroups();
  const groupIndex = useGroupIndex();
  const orgRefs = useOrgRefs(url.filters.organization);
  const { data: expertises } = useExpertises();
  const { data: programmationData = [] } = useProgrammations();
  const { data: eventsData = [] } = useEvents();
  const facets = useQuery({ queryKey: ["person-facets"], queryFn: async () => (await api.get("/persons/facets")).data });

  const programmationMap = useMemo(() => {
    const map = {};
    for (const item of programmationData) {
      map[item.id] = item;
    }
    return map;
  }, [programmationData]);

  const eventsMap = useMemo(() => {
    const map = {};
    for (const item of eventsData) {
      map[item.id] = item;
    }
    return map;
  }, [eventsData]);

  const programmationOptions = useMemo(() => {
    return programmationData.map((p) => ({
      value: p.id,
      label: p.title,
      subtitle: p.type || "Session",
    }));
  }, [programmationData]);

  const eventsOptions = useMemo(() => {
    return eventsData.map((e) => {
      let dateLabel = "";
      if (e.date) {
        try {
          const parts = e.date.split("T")[0].split("-");
          if (parts.length === 3) {
            dateLabel = `${parts[2]}/${parts[1]}/${parts[0]}`;
          } else {
            dateLabel = e.date;
          }
        } catch {
          dateLabel = e.date;
        }
      }
      return {
        value: e.id,
        label: e.title,
        subtitle: dateLabel || (e.format && e.format[0]) || "",
      };
    });
  }, [eventsData]);

  const hasAnimOrInterv = url.filters.activites.includes("Animation") || url.filters.activites.includes("Intervention");
  const hasParticipation = url.filters.activites.includes("Participation");

  const handleActivityChange = (newActs) => {
    url.setValues("activites", newActs);
    const stillHasAnimOrInterv = newActs.includes("Animation") || newActs.includes("Intervention");
    const stillHasParticipation = newActs.includes("Participation");
    if (!stillHasAnimOrInterv && url.filters.programmation_ids?.length > 0) {
      url.setValues("programmation_ids", []);
    }
    if (!stillHasParticipation && url.filters.evenement_ids?.length > 0) {
      url.setValues("evenement_ids", []);
    }
  };

  const filterQs = toQuery({ ...url.filters, q: url.q, job, ville });
  const qs = `${filterQs}&${toQuery({ page: url.page, page_size: PAGE_SIZE })}`;

  const list = useQuery({
    queryKey: ["persons", qs, isAuthed],
    queryFn: async () => (await api.get(`/persons?${qs}`)).data,
    placeholderData: keepPreviousData,
  });

  const items = list.data?.items || [];
  const total = list.data?.total || 0;

  const toggle = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const pageIds = items.map((p) => p.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const togglePage = () => setSelected((prev) => {
    const n = new Set(prev);
    pageIds.forEach((id) => (allPageSelected ? n.delete(id) : n.add(id)));
    return n;
  });

  const selectAll = async () => {
    try {
      const { data } = await api.get(`/persons/ids?${filterQs}`);
      setSelected((prev) => new Set([...prev, ...data.ids]));
      toast.success(`${formatNumber(data.total)} contacts sélectionnés`);
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  const chips = [
    ...url.filters.organization.map((id) => ({
      key: "organization",
      value: id,
      group: "Entreprise",
      label: orgRefs.data?.find((o) => o.id === id)?.name || "…",
    })),
    ...(job ? [{ key: "job", value: job, group: "Fonction", label: job }] : []),
    ...url.filters.expertise.map((v) => ({ key: "expertise", value: v, group: "Expertise", label: v })),
    ...url.filters.business_department.map((v) => ({ key: "business_department", value: v, group: "Département métier", label: v })),
    ...url.filters.profile_type.map((v) => ({ key: "profile_type", value: v, group: "Type de profil", label: v })),
    ...url.filters.activites.map((v) => ({ key: "activites", value: v, group: "Activité", label: v })),
    ...url.filters.programmation_ids.map((id) => ({
      key: "programmation_ids",
      value: id,
      group: "Programmation",
      label: programmationMap[id]?.title || "Session…",
    })),
    ...url.filters.evenement_ids.map((id) => ({
      key: "evenement_ids",
      value: id,
      group: "Événement",
      label: eventsMap[id]?.title || "Événement…",
    })),
    ...url.filters.club.map((id) => ({ key: "club", value: id, group: "Club", label: groupIndex[id]?.name || "…" })),
    ...url.filters.commission.map((id) => ({ key: "commission", value: id, group: "Commission", label: groupIndex[id]?.name || "…" })),
    ...url.filters.membership.map((v) => ({ key: "membership", value: v, group: "Adhésion", label: v })),
    ...(ville ? [{ key: "ville", value: ville, group: "Ville", label: ville }] : []),
    ...url.filters.geo_department.map((v) => ({ key: "geo_department", value: v, group: "Département", label: v })),
  ];

  const handleRemoveChip = (chip) => {
    if (chip.key === "job") {
      url.setParam("job", "");
    } else if (chip.key === "ville") {
      url.setParam("ville", "");
      url.setParam("city", "");
    } else if (chip.key === "activites") {
      const remainingActs = url.filters.activites.filter((x) => x !== chip.value);
      url.setValues("activites", remainingActs);
      const stillHasAnimOrInterv = remainingActs.includes("Animation") || remainingActs.includes("Intervention");
      const stillHasParticipation = remainingActs.includes("Participation");
      if (!stillHasAnimOrInterv && url.filters.programmation_ids?.length > 0) {
        url.setValues("programmation_ids", []);
      }
      if (!stillHasParticipation && url.filters.evenement_ids?.length > 0) {
        url.setValues("evenement_ids", []);
      }
    } else {
      url.removeValue(chip.key, chip.value);
    }
  };

  const groupOptions = (kind) => (groups?.[kind] || []).filter((g) => g.active).map((g) => ({ value: g.id, label: g.name, color: g.color }));

  let content;
  if (list.isLoading) content = <LoadingGrid count={9} />;
  else if (list.isError) content = <ErrorState onRetry={list.refetch} />;
  else if (!items.length) {
    content = (
      <EmptyState
        description="Essayez d'élargir votre recherche ou de retirer un filtre."
        action={isAuthed && <Button asChild className="rounded-full" data-testid="empty-add-person-button"><Link to="/personnes/nouveau"><UserPlus className="h-4 w-4" /> Ajouter une personne</Link></Button>}
      />
    );
  } else if (view === "table") {
    content = <PersonTable persons={items} selectable={isAuthed} selectedIds={selected} onToggle={toggle} internal={isAuthed} />;
  } else {
    content = (
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="persons-grid">
        {items.map((p) => <PersonCard key={p.id} person={p} selectable={isAuthed} selected={selected.has(p.id)} onToggle={toggle} />)}
      </div>
    );
  }

  return (
    <div className="lm-container pb-32">
      <PageHeader
        eyebrow="Répertoire"
        title="Personnes"
        icon={Users}
        color="#2563EB"
        description="Recherchez, filtrez et sélectionnez les contacts de l'écosystème La Mêlée."
        actions={isAuthed && (
          <Button asChild className="rounded-full px-5 font-semibold" data-testid="add-person-button">
            <Link to="/personnes/nouveau"><UserPlus className="h-4 w-4" /> Ajouter une personne</Link>
          </Button>
        )}
      />

      <div className="lm-card space-y-4 p-4 sm:p-5" data-testid="persons-filter-bar">
        {/* Ligne 1 : Recherche globale & Vue Grille/Tableau */}
        <div className="flex flex-col gap-3 md:flex-row md:items-center">
          <SearchInput
            className="flex-1"
            value={url.q}
            onChange={(v) => url.setParam("q", v)}
            testId="persons-search-input"
            placeholder="Nom, fonction, entreprise, ville, communauté…"
          />
          <ViewToggle value={view} onChange={setView} />
        </div>

        {/* Ligne 2 : Filtres actifs (Tags / Badges) avec suppression individuelle et Réinitialiser les filtres */}
        <ActiveChips chips={chips} onClear={url.clearAll} onRemove={handleRemoveChip} />

        {/* Ligne 3 : Bouton Masquer / Afficher les filtres */}
        <div className="flex items-center justify-between border-t border-border/60 pt-3">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowFilters((prev) => !prev)}
            className="h-8 gap-1.5 rounded-full px-3.5 text-xs font-semibold text-stone-700 hover:text-stone-900 border-border bg-white hover:bg-stone-50 transition-colors"
            data-testid="toggle-filters-button"
          >
            <SlidersHorizontal className="h-3.5 w-3.5 text-primary" />
            {showFilters ? "Masquer les filtres" : "Afficher les filtres"}
          </Button>

          {chips.length > 0 && !showFilters && (
            <span className="text-xs font-medium text-stone-500">
              {chips.length} filtre{chips.length > 1 ? "s" : ""} actif{chips.length > 1 ? "s" : ""}
            </span>
          )}
        </div>

        {/* 4 Sections Thématiques compactes et aérées, visibles par défaut */}
        {showFilters && (
          <div className="grid gap-3.5 pt-1 sm:grid-cols-2" data-testid="filter-sections">
            {/* Section 1 — Métiers & compétences : Fonction, Expertise, Département métier */}
            <div className="rounded-xl border border-border/70 bg-stone-50/60 p-3.5 space-y-2.5 transition-all">
              <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-stone-600">
                <Briefcase className="h-3.5 w-3.5 text-primary" />
                <span>Section 1 — Métiers & compétences</span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <SearchInput
                  className="w-full sm:w-48"
                  value={job}
                  onChange={(v) => url.setParam("job", v)}
                  testId="filter-job-input"
                  placeholder="Fonction (ex : RSSI)"
                />
                <MultiSelectFilter
                  label="Expertise"
                  icon={Tag}
                  selected={url.filters.expertise}
                  onChange={(v) => url.setValues("expertise", v)}
                  options={(expertises || []).map((e) => ({ value: e.name, label: e.name, color: e.color }))}
                  testId="filter-expertise"
                />
                <MultiSelectFilter
                  label="Département métier"
                  icon={Network}
                  selected={url.filters.business_department}
                  onChange={(v) => url.setValues("business_department", v)}
                  options={(facets.data?.business_department || []).map((v) => ({ value: v, label: v }))}
                  testId="filter-business_department"
                />
              </div>
            </div>

            {/* Section 2 — Profil & organisation : Type de profil, Entreprise */}
            <div className="rounded-xl border border-border/70 bg-stone-50/60 p-3.5 space-y-2.5 transition-all">
              <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-stone-600">
                <Building2 className="h-3.5 w-3.5 text-primary" />
                <span>Section 2 — Profil & organisation</span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <MultiSelectFilter
                  label="Type de profil"
                  icon={UserCog}
                  selected={url.filters.profile_type}
                  onChange={(v) => url.setValues("profile_type", v)}
                  options={(facets.data?.profile_type || []).map((v) => ({ value: v, label: v }))}
                  testId="filter-profile_type"
                />
                <OrgFilter
                  selected={url.filters.organization}
                  onChange={(v) => url.setValues("organization", v)}
                  icon={Building2}
                />
              </div>
            </div>

            {/* Section 3 — Activité & réseau La Mêlée : Activité La Mêlée, Club, Commission, Adhésion */}
            <div className="rounded-xl border border-border/70 bg-stone-50/60 p-3.5 space-y-3 transition-all">
              <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-stone-600">
                <Activity className="h-3.5 w-3.5 text-primary" />
                <span>Section 3 — Activité & réseau La Mêlée</span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <MultiSelectFilter
                  label="Activité La Mêlée"
                  icon={Activity}
                  selected={url.filters.activites}
                  onChange={handleActivityChange}
                  options={ACTIVITE_OPTIONS}
                  renderLabel={(count) =>
                    count === 0
                      ? "Activité La Mêlée"
                      : count === 1
                      ? "1 activité sélectionnée"
                      : `${count} activités sélectionnées`
                  }
                  testId="filter-activites"
                />
                <MultiSelectFilter
                  label="Club"
                  icon={UsersRound}
                  options={groupOptions("clubs")}
                  selected={url.filters.club}
                  onChange={(v) => url.setValues("club", v)}
                  testId="filter-club"
                />
                <MultiSelectFilter
                  label="Commission"
                  icon={Landmark}
                  options={groupOptions("commissions")}
                  selected={url.filters.commission}
                  onChange={(v) => url.setValues("commission", v)}
                  testId="filter-commission"
                />
                <MultiSelectFilter
                  label="Adhésion"
                  icon={BadgeCheck}
                  selected={url.filters.membership}
                  onChange={(v) => url.setValues("membership", v)}
                  options={(facets.data?.membership || []).map((v) => ({ value: v, label: v }))}
                  testId="filter-membership"
                />
              </div>

              {/* Sous-filtres contextuels et dynamiques pour Programmation et Événements */}
              {(hasAnimOrInterv || hasParticipation) && (
                <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-border/50 animate-in fade-in slide-in-from-top-1 duration-200">
                  {hasAnimOrInterv && (
                    <MultiSelectFilter
                      label="Programmation / Tables rondes"
                      icon={Calendar}
                      selected={url.filters.programmation_ids}
                      onChange={(v) => url.setValues("programmation_ids", v)}
                      options={programmationOptions}
                      searchPlaceholder="Rechercher une table ronde ou session…"
                      widthClass="w-80 sm:w-96"
                      renderLabel={(count) =>
                        count === 0
                          ? "Sélectionner une table ronde ou session..."
                          : count === 1
                          ? (programmationMap[url.filters.programmation_ids[0]]?.title || "1 session sélectionnée")
                          : `${count} sessions sélectionnées`
                      }
                      testId="filter-programmation"
                    />
                  )}
                  {hasParticipation && (
                    <MultiSelectFilter
                      label="Événement"
                      icon={Ticket}
                      selected={url.filters.evenement_ids}
                      onChange={(v) => url.setValues("evenement_ids", v)}
                      options={eventsOptions}
                      searchPlaceholder="Rechercher un événement…"
                      widthClass="w-80 sm:w-96"
                      renderLabel={(count) =>
                        count === 0
                          ? "Sélectionner un événement..."
                          : count === 1
                          ? (eventsMap[url.filters.evenement_ids[0]]?.title || "1 événement sélectionné")
                          : `${count} événements sélectionnés`
                      }
                      testId="filter-evenement"
                    />
                  )}
                </div>
              )}
            </div>

            {/* Section 4 — Localisation : Ville (champ texte), Département */}
            <div className="rounded-xl border border-border/70 bg-stone-50/60 p-3.5 space-y-2.5 transition-all">
              <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-stone-600">
                <MapPin className="h-3.5 w-3.5 text-primary" />
                <span>Section 4 — Localisation</span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <CityFilter
                  value={ville}
                  onChange={(v) => url.setParam("ville", v)}
                  testId="filter-city-input"
                  placeholder="Rechercher une ville…"
                />
                <MultiSelectFilter
                  label="Département"
                  icon={Map}
                  selected={url.filters.geo_department}
                  onChange={(v) => url.setValues("geo_department", v)}
                  options={(facets.data?.geo_department || []).map((v) => ({ value: v, label: v }))}
                  testId="filter-geo_department"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="mb-4 mt-6 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-stone-600" data-testid="results-count">
          <b className="font-heading text-lg text-stone-900">{formatNumber(total)}</b> personne{total > 1 ? "s" : ""}
        </p>
        {isAuthed && total > 0 && (
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <label className="flex cursor-pointer items-center gap-2 font-semibold">
              <Checkbox checked={allPageSelected} onCheckedChange={togglePage} data-testid="select-page-checkbox" /> Sélectionner la page
            </label>
            <button onClick={selectAll} className="font-bold text-primary hover:underline" data-testid="select-all-results-button">
              Sélectionner les {formatNumber(total)} résultats
            </button>
          </div>
        )}
      </div>
      <div className={list.isFetching && !list.isLoading ? "opacity-60 transition-opacity" : "transition-opacity"}>{content}</div>
      <Pager page={url.page} total={total} pageSize={PAGE_SIZE} onPage={url.setPage} />
      {isAuthed && <SelectionBar selectedIds={selected} onClear={() => setSelected(new Set())} />}
    </div>
  );
}
